// ============================================================================
// The Loose Ends report.
//
// Without this a half-finished offboarding is invisible: nobody goes looking
// for a card that was never cancelled on somebody who left in April. Seeded
// against the seven people who were already off-roster when this module landed.
//
// ⛔ A CLOSED FACILITY STILL NAMING A DEPARTED AM IS HISTORY. An OPEN one is a
// service gap. The two are counted separately on purpose, and an active company
// card on somebody who has left is never benign.
// ============================================================================

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, ErrorState, PageHeader, Skeleton } from '../../shared/components';
import { useLooseEnds } from './hooks/use-separation-data';
import { reasonLabel, type LooseEnd } from './types';

interface Finding {
  text: string;
  severe: boolean;
}

function findings(r: LooseEnd): Finding[] {
  const out: Finding[] = [];
  if (r.live_app_access > 0)
    out.push({ text: 'App access is still live', severe: true });
  if (r.active_cards > 0)
    out.push({
      text: `Company card ending ${r.active_card_last4} is still active — cancel at the bank`,
      severe: true,
    });
  if (r.facilities_open > 0)
    out.push({ text: `${r.facilities_open} open facilities still name them`, severe: true });
  if (r.route_stops > 0)
    out.push({ text: `${r.route_stops} active route stops`, severe: true });
  if (r.pipeline_cards > 0)
    out.push({ text: `${r.pipeline_cards} active pipeline cards`, severe: true });
  if (r.vehicles > 0) out.push({ text: `${r.vehicles} vehicle still assigned`, severe: true });
  if (r.open_tasks > 0) out.push({ text: `${r.open_tasks} open tasks`, severe: true });
  if (r.open_routings > 0)
    out.push({ text: `${r.open_routings} records routed to them`, severe: true });
  if (r.open_compensation > 0)
    out.push({ text: 'Compensation record never ended', severe: true });
  if (r.open_benefits > 0)
    out.push({ text: 'Benefit enrolment never terminated', severe: true });
  if (r.active_notifications > 0)
    out.push({ text: `${r.active_notifications} active notifications`, severe: true });
  if (r.record_follows > 0)
    out.push({ text: `${r.record_follows} records still followed`, severe: true });
  if (r.person_named_routing > 0)
    out.push({ text: 'A notification routing rule names them directly', severe: true });
  if (r.needs_record) out.push({ text: 'No separation record exists', severe: true });
  if (r.facilities_closed_benign > 0)
    out.push({
      text: `${r.facilities_closed_benign} CLOSED facilities still name them — history, not a gap`,
      severe: false,
    });
  return out;
}

export default function LooseEnds() {
  const { rows, loading, error } = useLooseEnds();
  if (error) return <ErrorState title="Couldn't load the Loose Ends report" body={error} />;

  const withIssues = rows.filter((r) => findings(r).some((f) => f.severe));

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <PageHeader
        eyebrow="People › Separations"
        title="Loose Ends"
        meta={
          loading
            ? 'Loading…'
            : `${rows.length} former employees · ${withIssues.length} still holding something that matters`
        }
      />
      {loading && <Skeleton className="h-40" />}
      {!loading && rows.length === 0 && (
        <Card className="p-6 text-sm text-tcs-text-secondary">
          Nothing to show. This report is visible to HR, the executives and Finance.
        </Card>
      )}
      {rows.map((r) => {
        const f = findings(r);
        const severe = f.filter((x) => x.severe);
        return (
          <Card key={r.employee_id} className="p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="text-sm font-medium text-tcs-text">
                  {r.employee_name ?? r.employee_id}
                </span>
                <span className="ml-2 text-xs text-tcs-text-muted">
                  {r.employee_title} · {reasonLabel(r.separation_type)}
                  {r.separation_date ? ` · left ${r.separation_date}` : ' · no date on file'}
                </span>
              </div>
              {severe.length === 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-sentiment-positive-text">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Clean
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-sentiment-critical-text">
                  <AlertTriangle className="h-3.5 w-3.5" /> {severe.length} to chase
                </span>
              )}
            </div>
            {f.length > 0 && (
              <ul className="mt-2 space-y-1">
                {f.map((x) => (
                  <li
                    key={x.text}
                    className={`text-xs ${x.severe ? 'text-sentiment-critical-text' : 'text-tcs-text-muted'}`}
                  >
                    {x.severe ? '• ' : '· '}
                    {x.text}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      })}
    </div>
  );
}
